import React from 'react';
import { Button } from './ui/button';
import { Heart, Brain, Bell, Sparkles, X } from 'lucide-react';
import { useLocale } from '../context/LocaleContext';

/**
 * Soft Subscription Ask - Shows on Day 2-3
 * Emotional, not salesy
 * "I've been remembering things for you. Want me to keep doing this?"
 */
const SoftSubscriptionAsk = ({ onSubscribe, onMaybeLater, userStats }) => {
  const { t } = useLocale();
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-2xl animate-scale-in relative">
        
        {/* Close button */}
        <button
          onClick={onMaybeLater}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Emotional header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-glow-purple">
            <Heart className="h-8 w-8 text-white" />
          </div>
          
          <h2 className="text-xl font-bold text-gray-800 mb-2">
            {t('settings.beenHereForYou') || "I've been here for you"}
          </h2>
          <p className="text-gray-500 text-sm">
            {t('settings.keepRemembering') || "Want me to keep remembering things?"}
          </p>
        </div>

        {/* What Allora has done */}
        {userStats && (
          <div className="bg-teal-50/50 rounded-xl p-4 mb-6">
            <p className="text-xs text-teal-600 font-medium mb-2">{t('settings.inLastFewDays') || "In the last few days, I've:"}</p>
            <ul className="space-y-1 text-sm text-gray-700">
              {userStats.memoriesCount > 0 && (
                <li>💜 {t('settings.rememberedThings') || `Remembered ${userStats.memoriesCount} thing${userStats.memoriesCount !== 1 ? 's' : ''} about you`}</li>
              )}
              {userStats.tasksCompleted > 0 && (
                <li>✅ {t('settings.helpedCompleteTasks') || `Helped you complete ${userStats.tasksCompleted} task${userStats.tasksCompleted !== 1 ? 's' : ''}`}</li>
              )}
              {userStats.remindersSet > 0 && (
                <li>⏰ {t('settings.setReminders') || `Set ${userStats.remindersSet} reminder${userStats.remindersSet !== 1 ? 's' : ''}`}</li>
              )}
              {userStats.nudgesSent > 0 && (
                <li>💡 Sent {userStats.nudgesSent} helpful nudge{userStats.nudgesSent !== 1 ? 's' : ''}</li>
              )}
            </ul>
          </div>
        )}

        {/* Benefits */}
        <div className="space-y-3 mb-6">
          <div className="flex items-center gap-3">
            <Brain className="h-5 w-5 text-teal-500" />
            <span className="text-sm text-gray-700">Keep all your memories</span>
          </div>
          <div className="flex items-center gap-3">
            <Bell className="h-5 w-5 text-teal-500" />
            <span className="text-sm text-gray-700">Smart reminders that actually help</span>
          </div>
          <div className="flex items-center gap-3">
            <Sparkles className="h-5 w-5 text-teal-500" />
            <span className="text-sm text-gray-700">Personalized support, always</span>
          </div>
        </div>

        {/* Price */}
        <div className="text-center mb-4">
          <p className="text-2xl font-bold text-gray-800">$9.99<span className="text-sm font-normal text-gray-500">/month</span></p>
          <p className="text-xs text-gray-500">Cancel anytime. No strings.</p>
        </div>

        {/* CTA */}
        <Button
          onClick={onSubscribe}
          className="w-full h-12 bg-gradient-to-r from-teal-600 to-cyan-500 hover:from-teal-700 hover:to-cyan-600 text-white rounded-xl font-medium shadow-glow-purple"
        >
          {t('settings.yesKeepHelping') || 'Yes, keep helping me'} 💜
        </Button>

        <button
          onClick={onMaybeLater}
          className="w-full text-center text-sm text-gray-500 hover:text-gray-700 mt-3 py-2"
        >
          {t('settings.maybeLater') || 'Maybe later'}
        </button>
      </div>
    </div>
  );
};

export default SoftSubscriptionAsk;